import { ChangeEvent, useMemo } from "react";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { resources } from "@constants/hardcoded-strings";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { LabelWidthTypes } from "@enums/label-width-types";
import { RelyingPartyDetailsCustomHostnamesValidationErrors } from "@idw-types/common/validation/relying-party/relying-party-details/relying-party-details-validation-errors";
import { FormattedRelyingPartyConfig } from "@idw-types/data/relying-party-config";

type RelyingPartyDetailsCustomHostnamesTabProps = {
    requestInProgress: boolean;
    state: FormattedRelyingPartyConfig;
    setState: (state: FormattedRelyingPartyConfig) => void;
    labelWidth: LabelWidthTypes;
    validationErrors: RelyingPartyDetailsCustomHostnamesValidationErrors;
}

export const RelyingPartyDetailsCustomHostnamesTab = (props: RelyingPartyDetailsCustomHostnamesTabProps) => {
    const {
        requestInProgress,
        state,
        labelWidth,
        validationErrors,
        setState
    } = props;

    const handleCustomAXNHostNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            customAXNHostName: event.target.value
        });
    };

    const handleCustomDCUIHostNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            customDCUIHostName: event.target.value
        });
    };

    const handleWhiteLabelKBAHostNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            whiteLabelKBAHostName: event.target.value
        });
    };

    const handleWhiteLabelAXNUI3HostNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            whiteLabelAXNUI3HostName: event.target.value
        });
    };

    const handleWhiteLabelKBA2HostNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            whiteLabelKBA2HostName: event.target.value
        });
    };

    const handleWhiteLabelAXNUIHostNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            whiteLabelAXNUIHostName: event.target.value
        });
    };

    const handleWhiteLabelDCUIHostNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            whiteLabelDCUIHostName: event.target.value
        });
    };

    const isUIDisabled = useMemo(() => {
        return requestInProgress || !state.permissions.canModify || !state.permissions.canModifyCustomHostnames;
    }, [requestInProgress, state.permissions.canModifyCustomHostnames]);

    const resource = resources.pages.relyingPartyDetails.tabs.customHostnames;

    return (
        <div>
            <ContentPageHeader title={resource.title} />
            <ContentPageBody isMaxWidth={true}>
                <InputFormGroup
                    controlId="relying-party-axn-input"
                    automationId={"relying-party-axn-input"}
                    label={resource.inputs.axn.label}
                    ariaLabel={resource.inputs.axn.label}
                    value={state.customAXNHostName || ""}
                    disabled={isUIDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.axn.placeholder}
                    onChange={handleCustomAXNHostNameChange}
                />
                {validationErrors.customAXNHostnameError}
                <InputFormGroup
                    controlId="relying-party-axnui-input"
                    automationId={"relying-party-axnui-input"}
                    label={resource.inputs.axnui.label}
                    ariaLabel={resource.inputs.axnui.label}
                    value={state.whiteLabelAXNUI3HostName || ""}
                    disabled={isUIDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.axnui.placeholder}
                    onChange={handleWhiteLabelAXNUI3HostNameChange}
                />
                {validationErrors.whiteLabelAXNUI3HostnameError}
                <InputFormGroup
                    controlId="relying-party-kbaui-input"
                    automationId={"relying-party-kbaui-input"}
                    label={resource.inputs.kbaui.label}
                    ariaLabel={resource.inputs.kbaui.label}
                    value={state.whiteLabelKBA2HostName || ""}
                    disabled={isUIDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.kbaui.placeholder}
                    onChange={handleWhiteLabelKBA2HostNameChange}
                />
                {validationErrors.whiteLabelKBA2HostnameError}
                <InputFormGroup
                    controlId="relying-party-dcui-input"
                    automationId={"relying-party-dcui-input"}
                    label={resource.inputs.dcui.label}
                    ariaLabel={resource.inputs.dcui.label}
                    value={state.whiteLabelDCUIHostName || ""}
                    disabled={isUIDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.dcui.placeholder}
                    onChange={handleWhiteLabelDCUIHostNameChange}
                />
                {validationErrors.whiteLabelDCUIHostnameError}
                <InputFormGroup
                    controlId="relying-party-axnui2-input"
                    automationId={"relying-party-axnui2-input"}
                    label={resource.inputs.axnui2.label}
                    ariaLabel={resource.inputs.axnui2.label}
                    value={state.whiteLabelAXNUIHostName || ""}
                    disabled={isUIDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.axnui2.placeholder}
                    onChange={handleWhiteLabelAXNUIHostNameChange}
                />
                {validationErrors.whiteLabelAXNUIHostnameError}
                <InputFormGroup
                    controlId="relying-party-kbaui2-input"
                    automationId={"relying-party-kbaui2-input"}
                    label={resource.inputs.kba.label}
                    ariaLabel={resource.inputs.kba.label}
                    value={state.whiteLabelKBAHostName || ""}
                    disabled={isUIDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.kba.placeholder}
                    onChange={handleWhiteLabelKBAHostNameChange}
                />
                <InputFormGroup
                    controlId="relying-party-dcui1-input"
                    automationId={"relying-party-dcui1-input"}
                    label={resource.inputs.dc.label}
                    ariaLabel={resource.inputs.dc.label}
                    value={state.customDCUIHostName || ""}
                    disabled={isUIDisabled}
                    isRequired={false}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.dc.placeholder}
                    onChange={handleCustomDCUIHostNameChange}
                />
                {validationErrors.customDCUIHostnameError}
            </ContentPageBody>
        </div>
    );
}